import { useState } from 'react';
import { Loader2, Wifi } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useAuthStore } from '@/lib/auth/store';
import { useCreateVoicePlan, usePulseDurations } from '../api';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddVoicePlanSheet({ open, onOpenChange }: Props) {
  const userId = useAuthStore((s) => s.userId);
  const create = useCreateVoicePlan();
  const pulses = usePulseDurations();

  const [planName, setPlanName] = useState('');
  const [pulseDuration, setPulseDuration] = useState('');
  const [pulsePrice, setPulsePrice] = useState('');

  const reset = () => {
    setPlanName('');
    setPulseDuration('');
    setPulsePrice('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!planName.trim()) return toast.error('Give the plan a name.');
    if (!pulseDuration) return toast.error('Pick a pulse duration.');
    if (!pulsePrice.trim()) return toast.error('Enter a pulse price.');

    create.mutate(
      {
        userId: Number(userId),
        planName: planName.trim(),
        pulseDuration,
        pulsePrice,
      },
      {
        onSuccess: (res) => {
          toast.success(res?.message ?? 'Voice plan created');
          reset();
          onOpenChange(false);
        },
        onError: () => toast.error('Could not create the voice plan.'),
      },
    );
  };

  return (
    <Sheet
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <SheetContent className="w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Wifi className="h-4 w-4" />
            New voice plan
          </SheetTitle>
          <SheetDescription>
            Define a pulse duration and the per-pulse price end users pay
            when this plan is assigned to them.
          </SheetDescription>
        </SheetHeader>
        <form className="mt-4 space-y-4" onSubmit={handleSubmit}>
          <div className="space-y-1.5">
            <Label htmlFor="plan-name">
              Plan name
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <Input
              id="plan-name"
              value={planName}
              onChange={(e) => setPlanName(e.target.value)}
              placeholder="e.g. 30sec_50p"
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label>
              Pulse duration
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <Select value={pulseDuration} onValueChange={setPulseDuration}>
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    pulses.isLoading
                      ? 'Loading…'
                      : (pulses.data ?? []).length === 0
                        ? 'No pulse durations configured'
                        : 'Pick a pulse duration'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {(pulses.data ?? []).map((p, i) => (
                  <SelectItem key={`${p.pulseDuration}-${i}`} value={p.pulseDuration}>
                    {p.pulseDuration}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pulse-price">
              Pulse price (paise)
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <Input
              id="pulse-price"
              inputMode="numeric"
              value={pulsePrice}
              onChange={(e) =>
                setPulsePrice(e.target.value.replace(/[^\d.]/g, ''))
              }
              placeholder="e.g. 12"
            />
            <p className="text-[10px] text-muted-foreground">
              Price billed per pulse. Use paise (Indian rupees ÷ 100).
            </p>
          </div>
          <SheetFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={create.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending && (
                <Loader2 className="h-4 w-4 animate-spin" />
              )}
              Create plan
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
